import React, { useState, useImperativeHandle, useCallback } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ReassignModal from 'feature/task/TaskNotify/ReassignModal';
import { uniqBy, size, get } from 'global/lib/lodash';
import { Button } from 'global/components/Button';
import { Row } from 'global/components/row';
import { Icon, Checkbox, FormControlLabel, Box, Typography } from '@mui/material';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';

// PFM-2564: Send Receipt forms (assessment status change, task status change)
// get an "Anyone with the link can view" checkbox so the underlying message
// flow can flag meta.anonymous_view and email external contacts.
export const UserSelectModal = React.forwardRef((props, ref) => {
  const [showModal, setShowModal] = useState({ open: false });
  // anyoneWithLink lives outside ReassignModal's formState since this checkbox
  // sits adjacent to the form rather than being one of its inputs.
  const [anyoneWithLink, setAnyoneWithLink] = useState(false);

  const [userParams] = useState({
    waitFor: props.realm_id,
    build: ['users'],
    params: { filter: { content_realm_id: props.realm_id } },
    useCache: { reload: 1 },
  });

  const users = useRetrieveData({ url: '/users' }, userParams);

  const displayModal = useCallback(
    (targetStatus) => {
      return new Promise((resolve, reject) => {
        const assignees = uniqBy([...(users.data || [])], 'id');
        const statusTitle = get(
          {
            draft: 'Draft',
            review: 'Review',
            published: 'Final',
          },
          targetStatus,
          targetStatus
        );

        // Honor builder-level share-link policy for the initial state of the
        // "Anyone with the link" checkbox. PFM-2646: policy lives in the single
        // "Link Sharing" blob. getAccountSetting returns {} when not present.
        const ls = getAccountSetting(SETTINGS.linkSharing);
        const cfg = (ls && ls.default) || {};
        const allowMode = typeof cfg.allow_mode === 'string' ? cfg.allow_mode : cfg.enabled ? 'allowed-default-on' : 'not-allowed';
        const liveEnabled = allowMode !== 'not-allowed';
        setAnyoneWithLink(liveEnabled && allowMode === 'allowed-default-on');

        const onClose = (success) => {
          setShowModal({ open: false });
          document.getElementsByTagName('body')[0].style = '';
          if (!success) {
            reject(new Error('canceled'));
          }
        };

        const onSubmit = async (fs) => {
          onClose(true);
          // Resolve with anyoneWithLink so callers (StatusBar) can route the
          // /messages POST with meta.anonymous_view set appropriately.
          resolve({ ...fs, targetStatus });
        };

        setShowModal({
          open: true,
          title: 'Change Status to: ' + statusTitle,
          targetStatus,
          okButton: 'Update & Send',
          contentTitle: '',
          contentSub: 'Would you like to send an email receipt?',
          tasks: [{ assignees: props.assignees || [] }],
          assignees: assignees,
          showDueDate: false,
          showTaskDefaults: false,
          liveEnabled,
          assignedToProps: {
            label: 'Send To',
          },
          onClose,
          buttons: (p) => (
            <Row style={{ gap: 8 }}>
              {props.allowUpdateOnly && (
                <Button onClick={() => onSubmit({ ...p.formState, sendReceipt: false, anyoneWithLink: false })} loading={false}>
                  No Email
                </Button>
              )}
              <Button
                onClick={() => onSubmit({ ...p.formState, sendReceipt: true, anyoneWithLink })}
                disabled={!size(get(p, 'formState.assignedTo', []))}
                loading={false}
                icon={<Icon>email</Icon>}
              >
                Send Email
              </Button>
            </Row>
          ),
        });
      });
    },
    [props, users.data, anyoneWithLink]
  );

  useImperativeHandle(
    ref,
    () => ({
      displayModal,
    }),
    [displayModal]
  );

  // Build expiry helper text from builder default — keeps parity with
  // ShareLinkModal's "Expires in N days: <date>" copy. PFM-2646: read the
  // single "Link Sharing" blob.
  const lsExpiry = getAccountSetting(SETTINGS.linkSharing);
  const lsExpiryCfg = (lsExpiry && lsExpiry.default) || {};
  const defaultDays = Number(lsExpiryCfg.expiration_days != null ? lsExpiryCfg.expiration_days : 7) || 7;
  const expiryDate = (() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + defaultDays);
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  })();

  return (
    <ReassignModal
      {...showModal}
      extraContent={
        showModal.liveEnabled
          ? () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anyoneWithLink}
                      onChange={(e) => setAnyoneWithLink(e.target.checked)}
                      data-testid="receipt-anyone-checkbox"
                    />
                  }
                  label="Anyone with the link can view"
                />
                <Typography
                  variant="caption"
                  sx={{ ml: 4, mt: -0.5, fontStyle: 'italic', color: 'text.secondary' }}
                  data-testid="receipt-anyone-helper"
                >
                  {anyoneWithLink ? `Expires in ${defaultDays === 1 ? 'one day' : `${defaultDays} days`}: ${expiryDate}` : 'Only users with accounts can open'}
                </Typography>
              </Box>
            )
          : undefined
      }
    />
  );
});
